import React, { useState, useEffect } from 'react';
import PluginLogo from '@assets/logo/plugin-logo.svg?react';
import ContrastIcon from '@assets/icons/contrast.svg?react';
import LightnessIcon from '@assets/icons/lightness.svg?react';
import RandomIcon from '@assets/icons/random.svg?react';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { ArrowIcon } from '../ArrowIcon';
import styles from './PluginMockup.module.scss';
import demoSnapshot from '@/data/demo-snapshot.json';

const BASE_COLOR_NAMES = ['gray', 'red', 'orange', 'blue'] as const;

const BASE_COLOR_VARS: Record<string, string> = {
  gray: 'var(--base-black)',
  red: 'var(--base-red)',
  orange: 'var(--base-orange)',
  blue: 'var(--base-blue)',
};

export const PluginMockupShell: React.FC = () => {
  const { mode } = useThemeMode();
  const [sliderWidth, setSliderWidth] = useState(0);

  // Get colors from snapshot
  const snapshotData = mode === 'dark' ? demoSnapshot.dark : demoSnapshot.light;
  const colorsByName = snapshotData.colorsByName;
  const contrastRatios = demoSnapshot.contrastRatios;

  // Generate palette rows
  const paletteRows = BASE_COLOR_NAMES.map((colorName) => {
    const colors = colorsByName[colorName as keyof typeof colorsByName] || [];
    const sortedColors = [...colors].sort((a, b) => a.step - b.step).slice(0, 5);
    return {
      colorName,
      swatches: sortedColors.map((color) => color.value),
    };
  });

  // Calculate static handle positions
  const MIN_RATIO = 1.0;
  const MAX_RATIO = 21.0;
  const HANDLE_WIDTH = 16;
  const LOG_MAX = Math.log(MAX_RATIO / MIN_RATIO);

  const ratioToPosition = (ratio: number): number => {
    if (ratio <= MIN_RATIO) return 0;
    return (Math.log(ratio / MIN_RATIO) / LOG_MAX) * sliderWidth;
  };

  // Measure slider width on mount
  useEffect(() => {
    const slider = document.getElementById('shell-contrast-slider');
    if (slider) {
      setSliderWidth(slider.offsetWidth);
    }
  }, []);

  const Overlay: React.FC<{ variant: string; children: React.ReactNode }> = ({ variant, children }) => {
    const textContent: React.ReactNode[] = [];
    let arrowContent: React.ReactNode = null;
    
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === ArrowIcon) {
        arrowContent = child;
      } else {
        textContent.push(child);
      }
    });
    
    return (
      <div className={`${styles["sticker"]} ${styles[`sticker--${variant}`]} ${styles["sticker--shell"]}`}>
        {textContent}
        {arrowContent && (
          <div>
            {arrowContent}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles["plugin-mockup"]}>
      <div className={styles["plugin"]}>
        <Overlay variant="red">
          Try it!
          <ArrowIcon direction="down" to="down" />
        </Overlay>
        <div className={styles["plugin__header"]}>
          <PluginLogo className={styles["figma-logo"]} aria-hidden="true" />
          <div className={styles["plugin__text-node"]}>
            Color Scales I/O
          </div>
        </div>
        <div className={styles["plugin__nav"]}>
          <div className={styles["plugin__nav-item"]}>Import</div>
          <div className={`${styles["plugin__nav-item"]} ${styles["plugin__nav-item--active"]}`}>Edit</div>
          <div className={styles["plugin__nav-item"]}>Export</div>
        </div>
        <div className={styles["plugin__view"]}>
          <div className={styles["plugin__theme"]}>
            <div className={styles["plugin__text-node"]}>New theme</div>
            <div className={styles["plugin__theme-controls"]}>
              <button
                className={`${styles["plugin__button"]} ${styles["plugin__button--text"]}`}
                aria-disabled="true"
                tabIndex={-1}
              >
                <span className={styles["plugin__icon"]} aria-hidden="true">
                  <ContrastIcon />
                </span>
                WCAG 2
              </button>
              <div className={styles["settings-button-wrapper"]}>
                <button
                  className={`${styles["plugin__button"]} ${styles["plugin__button--settings"]}`}
                  aria-disabled="true"
                  tabIndex={-1}
                >
                  <span className={styles["plugin__icon"]}>
                    <LightnessIcon />
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className={styles["plugin__base-colors"]}>
            <div className={styles["plugin__base-colors-row"]}>
              <div className={styles["plugin__text-node"]}>Base colors</div>
            </div>
            <div className={styles["plugin__base-colors-row"]}>
              <div className={styles["plugin__base-colors-list"]}>
                {BASE_COLOR_NAMES.map((colorName) => (
                  <div
                    key={colorName}
                    role="img"
                    className={`${styles["plugin__swatch"]} ${styles["plugin__swatch--base"]}`}
                    style={{ backgroundColor: BASE_COLOR_VARS[colorName] }}
                  />
                ))}
              </div>
              <button
                className={`${styles["plugin__button"]} ${styles["plugin__button--random"]}`}
                aria-disabled="true"
                tabIndex={-1}
              >
                <span className={styles["plugin__icon"]}>
                  <RandomIcon />
                </span>
              </button>
            </div>
          </div>
          <div className={styles["plugin__contrast"]}>
            <div className={styles["plugin__contrast-row"]}>
              <div className={styles["plugin__text-node"]}>Contrast ratios</div>
            </div>
            <div 
              id="shell-contrast-slider"
              className={styles["plugin__contrast-slider"]}
              style={{
                background: mode === 'light' 
                  ? 'linear-gradient(to right, var(--white), var(--black))'
                  : 'linear-gradient(to right, var(--black), var(--white))'
              }}
            >
              {contrastRatios.map((ratio, index) => (
                <div
                  key={index}
                  className={styles["plugin__contrast-handle"]}
                  style={{ 
                    transform: `translateX(${ratioToPosition(ratio) - HANDLE_WIDTH / 2}px)`,
                    transition: 'transform 0.3s ease-out'
                  }}
                />
              ))}
            </div>
          </div>
          <div className={styles["plugin__palette"]}>
            {paletteRows.map((row, rowIndex) => (
              <div key={rowIndex} className={styles["plugin__palette-row"]}>
                {row.swatches.map((swatchColor, swatchIndex) => (
                  <div
                    key={swatchIndex}
                    role="img"
                    className={styles["plugin__palette-swatch"]}
                    style={{ backgroundColor: swatchColor }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PluginMockupShell;
